<template>
    <v-container>
        <page-header :title="`${mode === 'edit' ? 'Edit' : mode === 'show' ? '' : 'New'} Season`.trim()" icon="mdi-calendar" />
        <v-card class="form-card" elevation="1">
            <v-form :readonly="readOnly">
                <div class="form-section-title">
                    <v-icon small>mdi-calendar</v-icon>Season
                </div>
                <v-row>
                    <v-col cols="6">
                        <v-text-field
                            v-model="season.year"
                            outlined
                            dense
                            hide-details="auto"
                            label="Year"
                            type="Number"
                            :clearable="clearable"
                        />
                    </v-col>
                    <v-col cols="6">
                        <v-text-field
                            v-model="season.name"
                            outlined
                            dense
                            hide-details="auto"
                            label="Name"
                            :clearable="clearable"
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="4">
                        <date-time-picker
                            :date-label="'From Date'"
                            :date="startDate"
                            @setDate="setStartDate"
                        />
                    </v-col>
                    <v-col cols="4">
                        <date-time-picker
                            :date-label="'To Date'"
                            :date="endDate"
                            @setDate="setEndDate"
                        />
                    </v-col>
                    <v-col cols="4">
                        <v-select
                            v-model="season.type"
                            outlined
                            dense
                            hide-details="auto"
                            :items="seasonType"
                            :rules="rules"
                            menu-props="auto"
                            label="Type"
                        />
                    </v-col>
                </v-row>

                <template v-if="isAdmin">
                    <div class="form-section-title">
                        <v-icon small>mdi-cash-multiple</v-icon>Finance per match
                    </div>
                    <div class="text-caption grey--text text--darken-1 mb-3">
                        Estimated amounts for one match of this season, used for the financial figures on the dashboard. Staff payments are added to the cost separately.
                    </div>
                    <v-row>
                        <v-col cols="12" sm="6">
                            <v-text-field
                                v-model.number="season.matchRevenue"
                                outlined
                                dense
                                hide-details="auto"
                                type="number"
                                min="0"
                                prefix="€"
                                label="Revenue per match"
                                prepend-inner-icon="mdi-trending-up"
                            />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field
                                v-model.number="season.defaultMatchCost"
                                outlined
                                dense
                                hide-details="auto"
                                type="number"
                                min="0"
                                prefix="€"
                                label="Cost per match (default)"
                                hint="Used for every arena without its own cost below"
                                persistent-hint
                                prepend-inner-icon="mdi-trending-down"
                            />
                        </v-col>
                    </v-row>

                    <div class="form-section-title">
                        <v-icon small>mdi-stadium-outline</v-icon>Cost per arena
                    </div>
                    <amount-rows
                        v-model="season.arenaCosts"
                        :items="arenaOptions"
                        :item-text="arenaLabel"
                        key-field="arenaId"
                        label-field="arenaName"
                        amount-field="cost"
                        select-label="Arena"
                        amount-label="Cost per match"
                        add-label="Add arena"
                        empty-text="Every arena uses the default cost per match."
                        icon="mdi-stadium-outline"
                        :readonly="readOnly"
                    />
                </template>
            </v-form>
        </v-card>
        <div v-if="mode !== 'show'" class="form-actions">
            <v-spacer />
            <v-btn large text @click="$router.go(-1)">
                Cancel
            </v-btn>
            <v-btn :loading="loading" :disabled="shouldDisableSubmit()" class="white--text" large depressed color="black" @click="onSubmit">
                <v-icon left>mdi-content-save-outline</v-icon>Save
            </v-btn>
        </div>
        <alert :show-alert="showAlert" :message="message" :type="type" />
    </v-container>
</template>

<script>
    import PageHeader from '@/components/PageHeader'
    import seasonService from '@/services/season'
    import Alert from '@/components/Alert'
    import DateTimePicker from '@/components/DateTimePicker'
    import momentTimezone from 'moment-timezone'
    import AmountRows from '@/components/AmountRows'
    import arenaService from '@/services/arena'
    import { arenaLabel, selectableArenas } from '@/utils/arenas'

    export default {
        name: 'SeasonForm',
        components: { Alert, AmountRows, DateTimePicker, PageHeader },
        data: () => {
            return {
                showAlert: false,
                message: null,
                type: null,
                mode: null,
                readOnly: false,
                clearable: true,
                loading: false,
                startDate: null,
                endDate: null,
                startTime: null,
                endTime: null,
                season: {
                    matchRevenue: null,
                    defaultMatchCost: null,
                    arenaCosts: []
                },
                arenas: [],
                seasonId: null,
                rules: [
                    v => !!v || 'Field is required'
                ],
                seasonType: ['winter', 'summer']
            }
        },
        computed: {
            isAdmin() {
                return this.$store.getters.isAdmin
            },
            // Only active arenas can be picked, but keep the ones the season already has a cost for
            arenaOptions() {
                return selectableArenas(this.arenas, this.season.arenaCosts.map(row => row.arenaId))
            }
        },
        created() {
            this.mode = this.$route.params.mode
            if (this.mode === 'show') {
                this.readOnly = true
                this.clearable = false
            }
            // active arenas first, then the rest
            if (this.isAdmin) {
                arenaService.getArenas().then(resp => {
                    this.arenas = resp.data.content.slice().sort((a, b) => (b.active - a.active) || a.name.localeCompare(b.name))
                })
            }
            this.seasonId = this.$route.params.seasonId
            if (this.seasonId) {
                seasonService.getSeason(this.seasonId).then(resp => {
                    this.season = { ...resp.data, arenaCosts: resp.data.arenaCosts || [] }
                    this.startDate = momentTimezone(this.season.startDate).tz('Europe/Athens').format('YYYY-MM-DD').toString()
                    if (this.season.endDate) {
                        this.endDate = momentTimezone(this.season.endDate).tz('Europe/Athens').format('YYYY-MM-DD').toString()
                    }
                })
            }
        },
        methods: {
            arenaLabel,
            setAlert(message, type) {
                this.showAlert = true
                this.message = message
                this.type = type
                setTimeout(() => { this.showAlert = false }, 7000)
            },
            shouldDisableSubmit() {
                const incomplete = this.season.arenaCosts.some(row => !row.arenaId || row.cost === null || row.cost < 0)
                return !this.season.year || !this.startDate || !this.season.type || incomplete
            },
            setStartDate(date) {
                this.startDate = date
            },
            setEndDate(date) {
                this.endDate = date
            },
            onEdit() {
                this.$router.push(`/seasons/${this.seasonId}/edit`)
            },
            createSeason() {
                this.loading = true
                seasonService.createSeason(this.season).then(seasonResponse => {
                    if (seasonResponse.id) {
                        this.seasonId = seasonResponse.id
                    }
                    this.loading = false
                    this.$router.push(`/seasons`)
                }).catch((error) => {
                    this.loading = false
                    this.setAlert(error.response.data.message, 'error')
                })
            },
            editSeason() {
                this.loading = true
                seasonService.updateSeason(this.seasonId, this.season).then(seasonResponse => {
                    if (seasonResponse.id) {
                        this.seasonId = seasonResponse.id
                    }
                    this.loading = false
                    this.$router.push(`/seasons`)
                }).catch((error) => {
                    this.loading = false
                    this.setAlert(error.response.data.message, 'error')
                })
            },
            async onSubmit() {
                // an emptied amount field gives '' with v-model.number
                ['matchRevenue', 'defaultMatchCost'].forEach(field => {
                    if (this.season[field] === '') this.season[field] = null
                })
                this.season.startDate = momentTimezone(this.startDate).tz('Europe/Athens').startOf('day').utc().format('YYYY-MM-DD[T]HH:mm:ss[Z]').toString()
                if (this.endDate) {
                    this.season.endDate = momentTimezone(this.endDate).tz('Europe/Athens').endOf('day').utc().format('YYYY-MM-DD[T]HH:mm:ss[Z]').toString()
                }
                if (this.mode === 'edit') {
                    this.editSeason()
                } else if (this.mode === 'create') {
                    this.createSeason()
                }
            }
        }

    }
</script>
<style>
.tox .tox-editor-header {
    z-index: 0 !important;
}
.tox .tox-statusbar {
    display: none !important;
}

.tox .tox-toolbar--scrolling {
    flex-wrap: wrap !important;
}
.tox.tox-tinymce.tox-platform-touch {
    height: 40vh !important;
    min-height: 350px;
}
</style>
<style scoped>
.body-label {
    color: rgba(0, 0, 0, 0.6);
}
.body-editor {
    margin-bottom: 2vh;
}
.edit-button-wrapper {
    width: 100%;
    display: flex;
    justify-content: flex-end;
}

.submit-button-wrapper {
    width: 100%;
    display: flex;
    justify-content: center;
    margin-top: 2vh;
}

.img-preview {
    max-width: 70%;
    margin: 0 auto;
}

.image-input {
    padding: 20px;
    width: 100%;
    border-style: groove;
    border: #D3D3D3 solid 2px;
    border-radius: 15px 50px;
    margin-top: 1vh;
}

.green-border {
    border-color: green;
}

.delete-btn {
    display: flex;
    justify-content: flex-end;
}
</style>
